/**
 * Store tool: drag across placed buildings to bulk-store them in inventory.
 * Pointer down on a building starts a sweep; pointer move adds each new
 * building the cursor enters; pointer up commits the whole sweep as a
 * single removeManyToInventory command (so undo wipes the entire bulk
 * action in one Ctrl+Z).
 *
 * Single click + immediate release = stack-of-one sweep, which still
 * works fine — collapses to a one-id command.
 *
 * onCancel / onDeactivate drop the in-flight sweep and clear the red
 * preview overlay (Esc / tool switch).
 */
import type { ToolPointerHandlers } from '$lib/renderer'

export interface StoreToolContext {
  /** Commit the sweep — editor decides which command kind to dispatch
   *  (single `remove` for one building, `removeManyToInventory` for many). */
  commitSweep(buildingIds: number[]): void
  /** Repaint the red "to be stored" overlay over the given building ids. */
  markBuildingsForStore(ids: ReadonlySet<number>): void
  /** Clear the overlay. */
  clearStoreMarks(): void
}

export function makeStoreHandlers(ctx: StoreToolContext): ToolPointerHandlers {
  let isPainting = false
  let stored: Set<number> = new Set()

  function reset() {
    isPainting = false
    stored = new Set()
    ctx.clearStoreMarks()
  }

  return {
    onPointerDown(e) {
      // Left-click only. Middle-click pan is dispatched at the scene
      // level before this handler runs; right-click is ignored.
      if (e.button !== 0) return
      isPainting = true
      stored = new Set()
      if (e.target) {
        stored.add(e.target.building.id)
        ctx.markBuildingsForStore(stored)
      }
    },

    onPointerMove(e) {
      if (!isPainting) return
      if (!e.target) return
      if (stored.has(e.target.building.id)) return
      stored.add(e.target.building.id)
      ctx.markBuildingsForStore(stored)
    },

    onPointerUp(e) {
      if (e.button !== 0) return
      if (!isPainting) return
      const ids = Array.from(stored)
      reset()
      if (ids.length > 0) ctx.commitSweep(ids)
    },

    onCancel() {
      if (!isPainting) return false
      reset()
      return true
    },

    onDeactivate() {
      if (isPainting) reset()
    },
  }
}
